/**
 * Centralized PostHog feature flag registry, client hook, and server helper.
 *
 * All flag keys live here so they're discoverable, typed, and easy to
 * grep when cleaning up after full rollout.
 *
 * Client-side: `useFeatureFlag(FEATURE_FLAGS.YC_APPLICATION)`
 * Server-side (Labs): `assertLabsFeatureEnabled()` in src/lib/api/routers/labs/middleware.ts
 *
 * Labs feature flags are defined in src/constants/labs.ts; they are no longer
 * merged into FEATURE_FLAGS (individual labs-* PostHog flags are retired).
 * See docs/decisions/013-labs-feature-isolation.md for the Labs architecture.
 */
"use client";

import {
  useActiveFeatureFlags as usePostHogActiveFeatureFlags,
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  usePostHog,
} from "posthog-js/react";
import { useEffect, useState } from "react";
import { FEATURE_FLAGS, type FeatureFlagKey } from "./featureFlagKeys";
import {
  FLAG_OVERRIDE_COOKIE,
  FLAG_OVERRIDE_QUERY_PARAM,
  isFlagForceEnabled,
  resolveFlagOverrides,
} from "@/lib/featureFlags/overrides";

// ---------------------------------------------------------------------------
// Flag registry — re-exported from `./featureFlagKeys` (a non-"use client"
// file) so server modules can import the keys without crossing a client
// boundary. See `featureFlagKeys.ts` for the rationale.
// ---------------------------------------------------------------------------

export { FEATURE_FLAGS };
export type { FeatureFlagKey };

// ---------------------------------------------------------------------------
// Environment-based overrides (NEXT_PUBLIC_ so they're available client-side)
// Set NEXT_PUBLIC_FEATURE_FLAG_OVERRIDES="yc-application"
// to force-enable specific flags regardless of PostHog state.
// Special value: "labs-all" enables every labs-* flag.
// ---------------------------------------------------------------------------

const FLAG_OVERRIDES: ReadonlySet<string> = new Set(
  (process.env.NEXT_PUBLIC_FEATURE_FLAG_OVERRIDES ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
);

function isEnvOverridden(flag: string): boolean {
  if (FLAG_OVERRIDES.has(flag)) return true;
  if (FLAG_OVERRIDES.has("labs-all") && flag.startsWith("labs")) return true;
  return false;
}

// Force-OFF override (NEXT_PUBLIC_FEATURE_FLAG_DISABLES="task-roadmap"). Local
// escape hatch to disable a flag PostHog would otherwise enable for you — e.g.
// when you're in the Internal Users cohort and want to test the flag-off path.
// Takes precedence over every enable path (env force-on, ?flags= cookie, and
// PostHog). Symmetric with FLAG_OVERRIDES.
const FLAG_DISABLES: ReadonlySet<string> = new Set(
  (process.env.NEXT_PUBLIC_FEATURE_FLAG_DISABLES ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
);

function isEnvDisabled(flag: string): boolean {
  return FLAG_DISABLES.has(flag);
}

/** Read a cookie by name in the browser (empty string when absent / on server). */
function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]!) : "";
}

/**
 * Runtime override from the `?flags=` URL param or the persisted cookie
 * (allowlisted flags only — see @/lib/featureFlags/overrides). Lets internal
 * users force a flag ON in any environment for testing; the server honors the
 * same cookie on API requests.
 */
function isRuntimeOverridden(flag: string): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  // The URL param is AUTHORITATIVE when present — a present-but-invalid
  // `?flags=` must not fall back to a stale cookie (matches the cookie the sync
  // effect writes/clears). Consult the cookie only when the param is absent.
  if (params.has(FLAG_OVERRIDE_QUERY_PARAM)) {
    return isFlagForceEnabled(flag, params.get(FLAG_OVERRIDE_QUERY_PARAM));
  }
  return isFlagForceEnabled(flag, readCookie(FLAG_OVERRIDE_COOKIE));
}

/**
 * Persist a `?flags=` override into the cookie so it survives navigation and
 * rides on API requests. Call once on mount (see PostHogProvider). A present
 * `?flags=` with no allowlisted value clears the cookie; an absent param leaves
 * any existing cookie untouched. Stores the resolved REAL flag keys.
 */
export function syncFlagOverridesFromUrl(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (!params.has(FLAG_OVERRIDE_QUERY_PARAM)) return;
  const resolved = [...resolveFlagOverrides(params.get(FLAG_OVERRIDE_QUERY_PARAM))];
  if (resolved.length === 0) {
    document.cookie = `${FLAG_OVERRIDE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
    return;
  }
  // 4h TTL — a test toggle, not a durable setting.
  document.cookie = `${FLAG_OVERRIDE_COOKIE}=${encodeURIComponent(
    resolved.join(","),
  )}; path=/; max-age=${4 * 60 * 60}; SameSite=Lax`;
}

// ---------------------------------------------------------------------------
// Client hook
// ---------------------------------------------------------------------------

/**
 * Typed wrapper around PostHog's useFeatureFlagEnabled.
 *
 * Resolution order (first truthy wins):
 * 1. Environment variable override (NEXT_PUBLIC_FEATURE_FLAG_OVERRIDES)
 *    - "labs-all" expands to all labs-* prefixed flags
 * 2. PostHog flag evaluation
 *    - "labs-all" PostHog flag expands to all labs-* prefixed flags
 */
export function useFeatureFlag(flag: FeatureFlagKey): boolean {
  const posthogEnabled = useFeatureFlagEnabled(flag) === true;

  // 0. Force-OFF override (trumps every enable path, including force-on)
  if (isEnvDisabled(flag)) return false;

  // 1. Env override (trumps everything)
  if (isEnvOverridden(flag)) return true;

  // 2. Runtime override via ?flags= / cookie (allowlisted flags only)
  if (isRuntimeOverridden(flag)) return true;

  // 3. PostHog
  return posthogEnabled;
}

/**
 * True while PostHog hasn't resolved this flag yet (its value is `undefined`).
 * `useFeatureFlag` coerces that unresolved state to `false`, which can briefly
 * flip a `!flag` gate to the wrong branch on first render — use this to hold
 * SIDE-EFFECTS (job dispatch, generation) until the flag is actually known.
 * Overrides resolve synchronously, so an overridden flag is never "loading".
 */
export function useFeatureFlagLoading(flag: FeatureFlagKey): boolean {
  const raw = useFeatureFlagEnabled(flag);
  const posthog = usePostHog();
  const [flagsSettled, setFlagsSettled] = useState(false);
  useEffect(() => {
    // Fires when the flags request settles — on success, on terminal failure
    // (`errorsLoading`), and immediately if flags already loaded before mount.
    // Returns an unsubscribe (posthog-js 1.387.0).
    return posthog.onFeatureFlags(() => setFlagsSettled(true));
  }, [posthog]);
  if (isEnvOverridden(flag) || isRuntimeOverridden(flag)) return false;
  // PostHog only initializes when a key is configured (see PostHogProvider). With
  // no key `posthog.init` never runs, so `useFeatureFlagEnabled` stays `undefined`
  // forever — that's "unavailable, use the default", not "still loading". Reporting
  // `true` here would hang every flagLoading gate permanently: e.g. the eval card
  // withholds its OFF (pre-feature) body behind `!flagLoading`, leaving a positive
  // count badge over an empty accordion in any key-less environment (local dev).
  // Treat unavailable as resolved so gates fall through to their default branch.
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return false;
  // Once the flags response has arrived, a flag that's STILL `undefined` is
  // missing or inactive in this environment's PostHog project — posthog-js
  // returns `undefined` for such flags forever, even after flags load (LEV-1563).
  // That's the same "unavailable, use the default" state as the key-less case;
  // without this, the gate hangs in any environment whose project lacks the flag
  // (bit production for eval-tasks — then `task-founder-signal` — after PR #821
  // fixed only local dev).
  if (flagsSettled) return false;
  return raw === undefined;
}

/**
 * The subset of `flags` on for this viewer, each resolved exactly as
 * useFeatureFlag resolves one, and `loading` as useFeatureFlagLoading reads
 * for any of them. One PostHog subscription covers the whole list, so the
 * caller's hook count never depends on the list's length.
 *
 * Each flag's exposure event goes out as useFeatureFlag's does (posthog-js
 * sends it once per flag value).
 */
export function useActiveFeatureFlags(flags: readonly FeatureFlagKey[]): {
  active: FeatureFlagKey[];
  loading: boolean;
} {
  // Subscribed only so the hook re-renders when flags change. Its list is not
  // the answer: posthog-js seeds it from getFlags(), which keeps flags whose
  // value is `false` until the first onFeatureFlags callback filters them.
  usePostHogActiveFeatureFlags();
  const posthog = usePostHog();
  const [flagsSettled, setFlagsSettled] = useState(false);
  useEffect(() => posthog.onFeatureFlags(() => setFlagsSettled(true)), [posthog]);

  const active = flags.filter((flag) => {
    if (isEnvDisabled(flag)) return false;
    if (isEnvOverridden(flag) || isRuntimeOverridden(flag)) return true;
    return posthog.isFeatureEnabled(flag) === true;
  });
  // Same unavailable-means-resolved rules as useFeatureFlagLoading.
  const loading =
    !!process.env.NEXT_PUBLIC_POSTHOG_KEY &&
    !flagsSettled &&
    flags.some(
      (flag) =>
        !isEnvOverridden(flag) &&
        !isRuntimeOverridden(flag) &&
        posthog.isFeatureEnabled(flag, { send_event: false }) === undefined,
    );
  return { active, loading };
}

/**
 * LEV-998: client-side gate for the Applicants pipeline/block feature.
 * Returns true only when the `applicants-enabled` flag is on (or env-overridden).
 * When false (the default), the canvas hides existing Applicants blocks and
 * Orbit hides every add-applicant affordance.
 */
export function useApplicantsEnabled(): boolean {
  return useFeatureFlag(FEATURE_FLAGS.APPLICANTS_ENABLED);
}

/**
 * Labs-specific flag hook. Two PostHog flags, checked in order:
 * 1. labs-all (Internal Users) — payload "*" = all features
 * 2. labs (Labs cohort) — array payload = curated list
 * Env override "labs-all" enables everything for local dev.
 */
export function useLabsFeatureFlag(flag: string): boolean {
  const posthogLabsAll = !!useFeatureFlagEnabled("labs-all");
  const labsAllPayload = useFeatureFlagPayload("labs-all");
  const posthogLabs = !!useFeatureFlagEnabled("labs");
  const labsPayload = useFeatureFlagPayload("labs");

  if (isEnvOverridden(flag)) return true;
  if (posthogLabsAll) {
    if (labsAllPayload === "*") return true;
    if (Array.isArray(labsAllPayload) && (labsAllPayload as string[]).includes(flag)) return true;
  }
  if (posthogLabs && Array.isArray(labsPayload) && (labsPayload as string[]).includes(flag)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Labs composite hook
// ---------------------------------------------------------------------------

/**
 * Returns true if the user has ANY labs feature enabled.
 * Used to show/hide the Labs sidebar icon.
 * Simplified: if labs-all is on (env override or PostHog), at least one
 * feature is visible.
 */
export function useAnyLabsEnabled(): boolean {
  const posthogLabsAll = !!useFeatureFlagEnabled("labs-all");
  const posthogLabs = !!useFeatureFlagEnabled("labs");
  if (isEnvOverridden("labs-all")) return true;
  return posthogLabsAll || posthogLabs;
}
